// exp_PORTFOLIO-61587_file
import { FC, useMemo } from 'react';

import vacancyOfTheDayFormFieldTouch from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/vacancy_of_the_day/vacancy_of_the_day_form_field_touch';

import useTrls from 'hooks/translation/useTrls';

import { MultiSelectField } from 'components/VacancyOfTheDayBuilder/components/MultiSelectField';
import {
    formatVacancyOfTheDayIds,
    getVacancyOfTheDayIdSource,
    useVacancyOfTheDayAnalyticsParams,
} from 'components/VacancyOfTheDayBuilder/useVacancyOfTheDayAnalyticsParams';

import { ProfRoles, selectProfessions } from 'store/autoReducers';
import {
    selectVacancyOfTheDayErrors,
    selectVacancyOfTheDayRoles,
    setVacancyOfTheDayErrors,
    setVacancyOfTheDayRoles,
} from 'store/models/vacancyOfTheDay';
import { useDispatch, useSelector } from 'store/utils';

const TrlKeys = {
    title: 'VacancyOfTheDayPage.roles.title',
    placeholder: 'VacancyOfTheDayPage.roles.placeholder',
    error: 'VacancyOfTheDayPage.roles.error',
    select: 'VacancyOfTheDayPage.select.button',
    apply: 'VacancyOfTheDayPage.select.apply',
    reset: 'VacancyOfTheDayPage.select.reset',
    searchPlaceholder: 'VacancyOfTheDayPage.select.searchPlaceholder',
    emptyTitle: 'VacancyOfTheDayPage.select.empty.title',
    emptyDescription: 'VacancyOfTheDayPage.select.empty.description',
};

interface RoleOption {
    value: string;
    text: string;
}

const flattenRoles = (roles: ProfRoles[]): RoleOption[] =>
    roles.flatMap((role) =>
        role.professionalRoles && role.professionalRoles.length > 0
            ? flattenRoles(role.professionalRoles)
            : [{ value: String(role.id), text: role.name }]
    );

export const ProfessionalRoles: FC = () => {
    const roles = useSelector(selectVacancyOfTheDayRoles);
    const invalid = useSelector(selectVacancyOfTheDayErrors).roles;
    const profRoles = useSelector(selectProfessions);

    const dispatch = useDispatch();
    const trls = useTrls();
    const { params, initialRoles } = useVacancyOfTheDayAnalyticsParams();

    const options = useMemo(() => flattenRoles(profRoles), [profRoles]);

    return (
        <MultiSelectField
            title={trls(TrlKeys.title)}
            placeholder={trls(TrlKeys.placeholder)}
            errorMessage={trls(TrlKeys.error)}
            invalid={invalid}
            options={options}
            selected={roles}
            onChange={(ids) => {
                dispatch(setVacancyOfTheDayRoles(ids));
                if (ids.length > 0) {
                    dispatch(setVacancyOfTheDayErrors({ roles: false }));
                }
                vacancyOfTheDayFormFieldTouch({
                    ...params,
                    fieldName: 'professional_role',
                    geoIds: null,
                    geoSource: 'None',
                    roleIds: formatVacancyOfTheDayIds(ids),
                    roleSource: getVacancyOfTheDayIdSource(ids, initialRoles),
                });
            }}
            onBlur={() => dispatch(setVacancyOfTheDayErrors({ roles: roles.length === 0 }))}
            dataQa="vacancy-of-the-day-roles"
            selectLabel={trls(TrlKeys.select)}
            applyLabel={trls(TrlKeys.apply)}
            resetLabel={trls(TrlKeys.reset)}
            searchPlaceholder={trls(TrlKeys.searchPlaceholder)}
            emptyTitle={trls(TrlKeys.emptyTitle)}
            emptyDescription={trls(TrlKeys.emptyDescription)}
        />
    );
};
